import { Box, ButtonProps, Stack } from '@mui/material';
import React, { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from './Breadcrumb';
import Button from './common/Button';
import Card from './common/Card';
import { useTranslation } from 'react-i18next';

// --- Types ---
interface BreadcrumbItem {
  label: string;
  isActive?: boolean;
  onClick?: () => void;
}

export interface ActionItem extends Omit<ButtonProps, 'children' | 'variant'> {
  label: string;
  route?: string;
  onClick?: () => void;
  variant?: 'blue' | 'outlinedBlue' | 'red' | 'gray' | 'text';
}

interface PageHeaderProps {
  actions?: ActionItem[];
  options?: BreadcrumbItem[];
  resetFilters?: () => void;
  isFilterActive?: boolean;
}

/**
 * PageHeader Component
 * Displays breadcrumb navigation and action buttons.
 */
const PageHeader: FC<PageHeaderProps> = ({
  actions = [],
  options = [],
  isFilterActive,
  resetFilters,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const onClickHandle = (actn: ActionItem) => {
    if (actn?.route) {
      navigate(actn.route);
    }
    if (actn?.onClick) {
      actn.onClick();
    }
  };

  return (
    <Card
      sx={{
        padding: '30px 20px',
        display: 'flex',
        direction: 'row',
        alignItems: 'center',
        height: '29.600px',
        borderRadius: `${theme.borderRadius.xl}px`,
      }}
    >
      {/* Breadcrumb Options */}
      <Box flexGrow={1} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {options?.map((item, idx) => (
          <Breadcrumb breadcrumb={item} idx={idx} options={options} key={idx} />
        ))}
      </Box>

      {/* Action Buttons */}
      <Stack direction="row" gap="10px">
        {isFilterActive && (
          <Button
            onClick={resetFilters}
            variant={'red'}
            disableRipple
            disableElevation
            sx={{
              textTransform: 'capitalize',
              borderRadius: `${theme.borderRadius.full}px`,
              fontSize: '15px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: '5px 20px',
              color: theme.palette.menuIconColor,
            }}
          >
            {t('filters.clearAll')}
          </Button>
        )}
        {actions?.length > 0 &&
          actions?.map((action, idx) => (
            <Button
              key={idx}
              onClick={() => onClickHandle(action)}
              variant={action.variant || 'outlinedBlue'}
              disableRipple
              disableElevation
              sx={{
                textTransform: 'capitalize',
                borderRadius: `${theme.borderRadius.full}px`,
                fontSize: '0.875rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: '5px 20px',
                color:
                  action.variant === 'blue'
                    ? theme.palette.common.white
                    : theme.palette.menuIconColor,
                '&:hover': {
                  backgroundColor:
                    action.variant === 'blue'
                      ? theme.palette.primary.main
                      : theme.palette.common.white,
                },
              }}
            >
              {action.label}
            </Button>
          ))}
      </Stack>
    </Card>
  );
};

export default React.memo(PageHeader);
